import { z } from 'zod';

export const ServiceStatusSchema = z.enum([
  'PENDING_PAYMENT',
  'ACTIVE',
  'PAST_DUE',
  'SUSPENDED',
  'CANCELLED',
  'EXPIRED',
]);
export type ServiceStatus = z.infer<typeof ServiceStatusSchema>;

export const BillingStatusSchema = z.enum([
  'NONE',
  'CHECKOUT_PENDING',
  'AUTHENTICATED',
  'ACTIVE',
  'PAST_DUE',
  'PAUSED',
  'CANCELLED',
  'COMPLETED',
]);
export type BillingStatus = z.infer<typeof BillingStatusSchema>;

export const PaymentGatewaySchema = z.enum(['razorpay', 'stripe']);
export type PaymentGateway = z.infer<typeof PaymentGatewaySchema>;

/**
 * Read-shape used by the apps/api query layer + apps/landing — a projection
 * of the `subscriptions` table (see apps/api/src/db/schema/subscriptions.ts
 * for the full DB row), not the full row itself.
 */
export const SubscriptionSchema = z.object({
  subscriptionId: z.string(),
  planCode: z.string(),
  serviceStatus: ServiceStatusSchema,
  billingStatus: BillingStatusSchema,
  currentPeriodStart: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  /** Derived as `cancelAtPeriodEnd === false` — not a separate DB column. */
  isAutoRenewalEnabled: z.boolean(),
  /**
   * Whether auto-renewal can be switched back on from the UI. False only where renewal is
   * already off AND the gateway's cancellation is one-way (Razorpay), where restoring it
   * needs a new subscription and a fresh mandate rather than a toggle. Derived from
   * `GATEWAY_CONFIG` in the API's query layer — the frontend must never infer it from
   * `gateway`, which would put a gateway-name branch outside `lib/billing/`.
   */
  canEnableAutoRenewal: z.boolean(),
  gateway: PaymentGatewaySchema.nullable(),
  amountMinor: z.number().nullable(),
  currency: z.string().nullable(),
});
export type Subscription = z.infer<typeof SubscriptionSchema>;

/**
 * Body for POST /internal/test-scaffold/subscription-status — the only way prod e2e specs force
 * a subscription's status pair without waiting on a real gateway webhook. See apps/api/CLAUDE.md's
 * Test Scaffolding section for the safety gate (name-prefix check, secret header, enable flag).
 */
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

export const TestScaffoldSubscriptionStatusRequestSchema = z.object({
  propertyId: z.string(),
  serviceStatus: ServiceStatusSchema,
  billingStatus: BillingStatusSchema,
  /**
   * Optionally stamp the paid billing period alongside the status pair.
   *
   * Status alone is not enough to make a subscription look paid: the tasks reconciler and
   * `isEntitledToNewWork` both require `current_period_start IS NOT NULL`, so an ACTIVE row
   * without a period is a state production never produces and produces no monitoring task.
   * Supplying the period here lets a test reach "paid and staffed" through the real reconciler
   * instead of inserting a task row by hand.
   */
  currentPeriodStart: IsoDateSchema.optional(),
  /** Exclusive, matching `subscriptions.current_period_end`. */
  currentPeriodEnd: IsoDateSchema.optional(),
});
export type TestScaffoldSubscriptionStatusRequest = z.infer<
  typeof TestScaffoldSubscriptionStatusRequestSchema
>;

export const TestScaffoldSubscriptionStatusResponseSchema = z.object({
  ok: z.literal(true),
  subscriptionId: z.string(),
  serviceStatus: ServiceStatusSchema,
  billingStatus: BillingStatusSchema,
});
export type TestScaffoldSubscriptionStatusResponse = z.infer<
  typeof TestScaffoldSubscriptionStatusResponseSchema
>;

export type UpdateSubscriptionRequest = {
  serviceStatus?: ServiceStatus;
  billingStatus?: BillingStatus;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  isAutoRenewalEnabled?: boolean;
};
